/**
 * Dealer trust and contact view model (Milestone 11).
 * Delegates to canonical dealer profile contract (Dealer Profile M2).
 */
import {
  CONTACT_DISABLED_LABELS_ES,
  CONTACT_DISABLED_REASONS,
  DEALER_CONTACT_COPY_ES,
  DEALER_TRUST_NOTES_ES,
  DEALER_VERIFICATION_DESCRIPTIONS_ES,
  DEALER_VERIFICATION_LABELS_ES,
} from "../constants/automapDealerTrustConstants";
import { DEALER_VERIFICATION_STATUS } from "../constants/automapListingConstants";
import { isValidWhatsappNumber, normalizeWhatsappNumber } from "./dealerWhatsapp";
import { listingHasDealerGap } from "./automapListing/listingVisibility";
import {
  normalizeDealerProfileFromSources,
  toDealerTrustViewModel as toProfileTrustViewModel,
  getSafeDealerProfileHref,
} from "./dealerProfile";
import {
  canContactDealerForListing as canContactProfileForListing,
  getDealerContactDisabledReason,
  getContactDisabledLabel as getProfileContactDisabledLabel,
  toLegacyContactDisabledReason,
} from "./dealerProfile/dealerContactRules";
import { normalizeDealerVerificationStatus } from "./dealerProfile/dealerVisibility";

function normalizeVerificationStatus(status, dealerVerified) {
  return normalizeDealerVerificationStatus({ verificationStatus: status, dealerVerified });
}

export function getDealerVerificationLabel(status) {
  const key = normalizeVerificationStatus(status);
  return DEALER_VERIFICATION_LABELS_ES[key] || DEALER_VERIFICATION_LABELS_ES.unknown;
}

export function getDealerVerificationDescription(status) {
  const key = normalizeVerificationStatus(status);
  return DEALER_VERIFICATION_DESCRIPTIONS_ES[key] || DEALER_VERIFICATION_DESCRIPTIONS_ES.unknown;
}

export function getDealerProfileHref(dealer, { slug = "automap", usePublicRoute = true } = {}) {
  return getSafeDealerProfileHref(dealer, { slug, usePublicRoute });
}

export function normalizeDealerWhatsapp(value) {
  return normalizeWhatsappNumber(value);
}

export function getContactDisabledReason(listing, dealer = null) {
  if (!listing) return CONTACT_DISABLED_REASONS.UNKNOWN;
  const profile = normalizeDealerProfileFromSources({ rawDealer: dealer, listing });
  const reason = getDealerContactDisabledReason(profile, listing);
  return toLegacyContactDisabledReason(reason) || reason;
}

export function canContactDealerForListing(listing, dealer = null) {
  const profile = normalizeDealerProfileFromSources({ rawDealer: dealer, listing });
  return canContactProfileForListing(profile, listing);
}

function resolveCatalogLabel(t, key, fallback) {
  if (!t) return fallback;
  const value = t(key);
  return value && value !== key ? value : fallback;
}

export function getContactDisabledLabel(reason, { t = null } = {}) {
  if (!reason) return null;
  if (t) {
    return resolveCatalogLabel(
      t,
      `automotive.detail.dealerTrust.contactDisabled.${reason}`,
      CONTACT_DISABLED_LABELS_ES[reason] ||
        getProfileContactDisabledLabel(reason) ||
        CONTACT_DISABLED_LABELS_ES.unknown
    );
  }
  return (
    CONTACT_DISABLED_LABELS_ES[reason] ||
    getProfileContactDisabledLabel(reason) ||
    CONTACT_DISABLED_LABELS_ES.unknown
  );
}

export function getDealerTrustNote({ isVerified, verificationStatus } = {}, { t = null } = {}) {
  if (t) {
    if (isVerified) {
      return t("automotive.detail.dealerTrust.trustNotes.verified");
    }
    if (verificationStatus === DEALER_VERIFICATION_STATUS.UNVERIFIED) {
      return t("automotive.detail.dealerTrust.trustNotes.unverified");
    }
    return t("automotive.detail.dealerTrust.trustNotes.default");
  }
  if (isVerified) return DEALER_TRUST_NOTES_ES.verified;
  if (verificationStatus === DEALER_VERIFICATION_STATUS.UNVERIFIED) {
    return DEALER_TRUST_NOTES_ES.unverified;
  }
  return DEALER_TRUST_NOTES_ES.default;
}

export function getDealerVerificationLabelLocalized(status, { t = null } = {}) {
  const key = normalizeVerificationStatus(status);
  if (t) {
    const catalogKey =
      key === DEALER_VERIFICATION_STATUS.VERIFIED
        ? "automotive.badges.dealerVerifiedFull"
        : key === DEALER_VERIFICATION_STATUS.PENDING
          ? "automotive.badges.dealerPending"
          : `automotive.detail.dealerTrust.verification.${key}`;
    return resolveCatalogLabel(t, catalogKey, DEALER_VERIFICATION_LABELS_ES[key] || DEALER_VERIFICATION_LABELS_ES.unknown);
  }
  return DEALER_VERIFICATION_LABELS_ES[key] || DEALER_VERIFICATION_LABELS_ES.unknown;
}

export function getDealerTrustViewModel(listing, dealerOverride = null, { slug = "automap", t = null } = {}) {
  const profile = normalizeDealerProfileFromSources({ rawDealer: dealerOverride, listing }, { slug, listing, usePublicRoute: true });
  const contactDisabledReason = getDealerContactDisabledReason(profile, listing);
  const legacyReason = toLegacyContactDisabledReason(contactDisabledReason);
  const canContact = contactDisabledReason == null;
  const trustVm = toProfileTrustViewModel(profile, { slug, listing });

  return {
    ...trustVm,
    dealerProfileHref: getSafeDealerProfileHref(profile, { slug, usePublicRoute: true, listing }),
    profileHref: getSafeDealerProfileHref(profile, { slug, usePublicRoute: true, listing }),
    whatsapp: profile.whatsapp,
    phone: profile.phone,
    dealerWhatsapp: profile.whatsapp,
    dealerPhone: profile.phone,
    dealerCity: profile.city,
    dealerNeighborhood: profile.neighborhood,
    dealerResponseTimeLabel: profile.responseTimeLabel,
    dealerStatus: profile.dealerStatus,
    dealerPublicStatus: profile.publicStatus,
    dealerVerified: profile.isVerified,
    canContact,
    contactDisabledReason: legacyReason,
    contactDisabledLabel: getContactDisabledLabel(legacyReason, { t }),
    disabledReason: legacyReason,
    contactCtaLabel: canContact
      ? t
        ? t("automotive.detail.layout.contactWhatsapp")
        : DEALER_CONTACT_COPY_ES.contactWhatsapp
      : t
        ? t("automotive.detail.layout.contactUnavailable")
        : DEALER_CONTACT_COPY_ES.contactUnavailable,
    profileCtaLabel: t
      ? t("automotive.detail.dealerTrust.viewDealerProfile")
      : DEALER_CONTACT_COPY_ES.viewProfile,
    trustNote: getDealerTrustNote(
      {
        isVerified: profile.isVerified,
        verificationStatus: profile.verificationStatus,
      },
      { t }
    ),
    verificationLabel: getDealerVerificationLabelLocalized(profile.verificationStatus, { t }),
    hasDealerGap: listingHasDealerGap(listing),
    showVerificationBadge: trustVm.showVerificationBadge,
  };
}

/** Re-export canonical profile helpers for gradual migration. */
export {
  normalizeDealerProfile,
  normalizeDealerProfileFromApi,
  normalizeDealerProfileFromListing,
  toDealerContactViewModel as toDealerProfileContactViewModel,
  toDealerInventorySummary,
  toDealerSeoViewModel,
  buildDealerAnalyticsPayload as buildDealerProfileAnalyticsPayload,
  isDealerPubliclyVisible,
  shouldShowDealerProfile,
} from "./dealerProfile";
